import { getScoreColor, getConfidenceColor, getEvidenceColor } from '../utils/scoring';
import TickSlider from './TickSlider';

export default function PillarScore({ pillar, data, onChange }) {
  const color = getScoreColor(data.score);

  const handleChange = (field, value) => {
    onChange(pillar.id, { ...data, [field]: value });
  };

  return (
    <div className={`card p-4 sm:p-5 ${color.border} border animate-fade-in-up`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-3 sm:mb-4">
        <div className="flex-1">
          <h3 className="font-semibold text-white text-sm sm:text-sm">
            {pillar.name}
            {(data.aiGenerated || data.aiSuggested) && (
              <span className="text-[9px] text-brand-gold ml-2 font-normal">
                AI: {(data.aiSuggested || data.score || 0).toFixed(1)}
              </span>
            )}
          </h3>
          <p className="text-xs text-neutral-500 mt-0.5">{pillar.description}</p>
          <span className="text-[10px] text-neutral-600 mt-1 block">Weight: {(pillar.weight * 100).toFixed(0)}%</span>
        </div>
        <div className="flex flex-col items-end flex-shrink-0 ml-3">
          <div className={`w-14 h-14 rounded-xl ${color.bg} flex items-center justify-center`}>
            <span className={`text-xl font-black ${color.text}`}>{data.score.toFixed(1)}</span>
          </div>
        </div>
      </div>

      {/* Score Slider */}
      <div className="mb-4">
        <TickSlider value={data.score} onChange={(v) => handleChange('score', v)} showValue={false} />
      </div>

      {/* AI Research — read-only, always visible when available */}
      {(data.aiRationale || (data.aiGenerated && data.rationale)) && (
        <div className="mb-3 bg-brand-gold/5 border border-brand-gold/20 rounded-lg p-3">
          <p className="text-[10px] text-brand-gold font-semibold mb-1 uppercase tracking-wider">AI Research</p>
          <p className="text-xs text-neutral-300 leading-relaxed">{data.aiRationale || data.rationale}</p>
        </div>
      )}

      {/* Rationale — editable by team */}
      <div className="mb-3">
        <label className="label-text">Team Rationale</label>
        <textarea
          value={data.rationale?.replace(/\n\n\[AI Research\]:.*$/s, '') || ''}
          onChange={(e) => handleChange('rationale', e.target.value)}
          placeholder="Add your team's notes on this score..."
          rows={2}
          className="input-field text-sm resize-none"
        />
      </div>

      {/* Confidence & Evidence — dropdowns change color */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label-text">Confidence</label>
          <select
            value={data.confidence}
            onChange={(e) => handleChange('confidence', e.target.value)}
            className={`w-full rounded-lg px-3 py-2.5 text-sm font-medium border focus:outline-none focus:ring-1 transition-all ${
              data.confidence === 'High' ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400 focus:ring-emerald-500/30' :
              data.confidence === 'Medium' ? 'bg-amber-500/10 border-amber-500/30 text-amber-400 focus:ring-amber-500/30' :
              'bg-red-500/10 border-red-500/30 text-red-400 focus:ring-red-500/30'
            }`}
          >
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
        </div>
        <div>
          <label className="label-text">Evidence</label>
          <select
            value={data.evidence}
            onChange={(e) => handleChange('evidence', e.target.value)}
            className={`w-full rounded-lg px-3 py-2.5 text-sm font-medium border focus:outline-none focus:ring-1 transition-all ${
              data.evidence === 'Observed' ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400 focus:ring-emerald-500/30' :
              data.evidence === 'Inferred' ? 'bg-amber-500/10 border-amber-500/30 text-amber-400 focus:ring-amber-500/30' :
              'bg-red-500/10 border-red-500/30 text-red-400 focus:ring-red-500/30'
            }`}
          >
            <option value="Observed">Observed</option>
            <option value="Inferred">Inferred</option>
            <option value="Missing Evidence">Missing Evidence</option>
          </select>
        </div>
      </div>
    </div>
  );
}
